/**
 * Cookie-consent state for the marketing site.
 *
 * Essential cookies always run; non-essential analytics (Application Insights,
 * which sets ai_user/ai_session, and Google Analytics 4, which sets _ga/_ga_*)
 * load ONLY after the visitor accepts — see main.tsx (initial load) and
 * CookieBanner (accept handler). PECR/GDPR require consent before non-essential
 * cookies are set, so telemetry stays gated.
 */
export const CONSENT_COOKIE = 'p365_cookie_consent';
export const OPEN_COOKIE_SETTINGS_EVENT = 'p365:open-cookie-settings';
export type ConsentValue = 'accepted' | 'rejected';

export function getConsent(): ConsentValue | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)p365_cookie_consent=(accepted|rejected)/);
  return match ? (match[1] as ConsentValue) : null;
}

function expireCookie(name: string, domain?: string): void {
  const domainAttribute = domain ? `; Domain=${domain}` : '';
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax${domainAttribute}`;
}

function cookieNames(): string[] {
  return document.cookie
    .split(';')
    .map((part) => part.trim().split('=')[0])
    .filter(Boolean);
}

/** Remove identifiers written by Application Insights and Google Analytics, including legacy visits. */
export function clearAnalyticsIdentifiers(): void {
  if (typeof document === 'undefined') return;
  const googleCookies = cookieNames().filter((name) => name === '_ga' || name.startsWith('_ga_') || name === '_gid');
  for (const name of ['ai_user', 'ai_session', 'ai_authUser', '_ga', '_gid', ...googleCookies]) {
    expireCookie(name);
    const hostname = window.location.hostname;
    if (hostname === 'provenance365.com' || hostname.endsWith('.provenance365.com')) {
      expireCookie(name, '.provenance365.com');
    }
    try {
      window.localStorage.removeItem(name);
      window.sessionStorage.removeItem(name);
    } catch {
      // Storage can be unavailable in privacy modes; cookie expiry still applies.
    }
  }
}

export function setConsent(value: ConsentValue): void {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  const maxAge = 60 * 60 * 24 * 180; // 180 days
  document.cookie = `${CONSENT_COOKIE}=${value}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
  if (value === 'rejected') clearAnalyticsIdentifiers();
}

export function hasAnalyticsConsent(): boolean {
  return getConsent() === 'accepted';
}

export function openCookieSettings(): void {
  window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS_EVENT));
}
